'use client';
import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from 'recharts';

const DATA = [
  { id: 'tpq-mcq', type: 'MCQ', seconds: 82, target: 90 },
  { id: 'tpq-tfng', type: 'TF/NG', seconds: 98, target: 90 },
  { id: 'tpq-ynng', type: 'YN/NG', seconds: 104, target: 90 },
  { id: 'tpq-minfo', type: 'Match Info', seconds: 88, target: 90 },
  { id: 'tpq-mhead', type: 'Match Head', seconds: 118, target: 90 },
  { id: 'tpq-mfeat', type: 'Match Feat', seconds: 95, target: 90 },
  { id: 'tpq-mend', type: 'Match End', seconds: 86, target: 90 },
  { id: 'tpq-comp', type: 'Completion', seconds: 91, target: 90 },
  { id: 'tpq-short', type: 'Short Ans', seconds: 79, target: 90 },
];

const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: Array<{ value: number }>; label?: string }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0].value;
  const over = val > 90;
  return (
    <div className="bg-card border border-border rounded-lg shadow-elevated p-3 text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      <p className={`font-bold tabular-nums ${over ? 'text-warning' : 'text-accent'}`}>
        {val}s avg
      </p>
      <p className="text-muted-foreground mt-0.5">{over ? `${val - 90}s over target` : `${90 - val}s under target`}</p>
    </div>
  );
};

export default function TimePerQuestionChart() {
  return (
    <div className="card-base p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="section-header">Avg Time / Question by Type</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Target: ≤90s per question · Red bars = over target</p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-1.5 rounded-full bg-accent inline-block" />
            <span className="text-muted-foreground">On target</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-1.5 rounded-full bg-warning inline-block" />
            <span className="text-muted-foreground">Over target</span>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={DATA} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
          <XAxis
            dataKey="type"
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            domain={[60, 130]}
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontFamily: 'var(--font-plus-jakarta-sans)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => `${v}s`}
          />
          <ReferenceLine
            y={90}
            stroke="var(--danger)"
            strokeDasharray="4 3"
            strokeWidth={1.5}
            label={{ value: '90s target', position: 'insideTopRight', fontSize: 10, fill: 'var(--danger)' }}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.5 }} />
          <Bar dataKey="seconds" radius={[4, 4, 0, 0]}>
            {DATA.map((entry) => (
              <Cell
                key={entry.id}
                fill={entry.seconds > 90 ? 'var(--warning)' : 'var(--accent)'}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-3 p-2.5 rounded-lg bg-warning-bg border border-warning/20">
        <p className="text-xs text-warning font-medium">
          Matching Headings (118s) and YN/NG (104s) are your two biggest time drains — target both in your next practice set
        </p>
      </div>
    </div>
  );
}